/**
 * Programme Viewer — a read-only snapshot of one programme and every module
 * in it: identity, versions and stages, PLOs, and the module table.
 *
 * @module components/reports/ProgrammeViewer
 */

import React, { useMemo } from "react";

import { ReportLoadError } from "@/components/reports/ReportLoadError";
import { recordVersionLabel } from "@/components/shared/revision-labels";
import { completionTone } from "@/components/shared/status-tones";
import { TONE_BADGE } from "@/components/shared/tone-presentation";
import {
  AsyncBoundary,
  type AsyncState,
  Badge,
  countLabel,
  EmptyState,
  HeroPanel,
  LinkButton,
  MutedText,
  Time,
} from "@/components/ui";
import { FormPanelBones } from "@/components/ui/SkeletonParts";
import { useReportScope } from "@/reports/ReportTenantScope";
import type { StoredProgramme } from "@/reports/types";
import { creditSystemName } from "@/utils/award-types";
import { completionPercent, validateProgramme } from "@/utils/validation";

import { ProgrammeModules } from "./ProgrammeModules";
import { ProgrammeOutcomes, ProgrammeVersions } from "./ProgrammeStructure";

export interface ProgrammeViewerProps {
  libraryId: string;
}

function HealthBadges({ stored }: { stored: StoredProgramme }): React.ReactElement {
  const p = stored.data;
  const flags = useMemo(() => validateProgramme(p), [p]);
  const completion = useMemo(() => completionPercent(p), [p]);
  const errors = flags.filter((f) => f.type === "error").length;
  const warnings = flags.filter((f) => f.type === "warn").length;
  const tone = completionTone(completion);

  return (
    <div className="flex flex-col items-end gap-2">
      <Badge tone={TONE_BADGE[tone]} size="sm">
        {completion}% complete
      </Badge>
      {errors > 0 && (
        <Badge tone="error" size="sm">
          {countLabel(errors, "error")}
        </Badge>
      )}
      {warnings > 0 && (
        <Badge tone="warning" size="sm">
          {countLabel(warnings, "warning")}
        </Badge>
      )}
      {errors === 0 && warnings === 0 && (
        <Badge tone="success" size="sm">
          Clean
        </Badge>
      )}
      <MutedText size="caption">
        {recordVersionLabel(stored.origin.revision)}, read{" "}
        <Time value={stored.syncedAt} variant="date" />
      </MutedText>
    </div>
  );
}

function ViewerBody({ stored }: { stored: StoredProgramme }): React.ReactElement {
  const p = stored.data;
  const creditUnit = creditSystemName(p.awardType, p.totalCredits);

  return (
    <div className="space-y-4" data-testid="programme-viewer">
      <LinkButton to="/reports/library" variant="ghost" icon="arrow-left" data-testid="viewer-back">
        Back to Library
      </LinkButton>

      <HeroPanel
        title={p.title || "Untitled Programme"}
        eyebrow={p.discipline}
        headingLevel={2}
        data-testid="identity-card"
        meta={
          <>
            {p.awardType && (
              <Badge tone="ink" variant="outline">
                {p.awardType}
              </Badge>
            )}
            {p.nfqLevel != null && (
              <Badge tone="ink" variant="outline">
                NFQ {p.nfqLevel}
              </Badge>
            )}
            <Badge tone="ink" variant="outline">
              {p.totalCredits ? `${p.totalCredits} ${creditUnit}` : "Credits not set"}
            </Badge>
            {(p.modules ?? []).length > 0 && (
              <Badge tone="ink" variant="outline">
                {countLabel((p.modules ?? []).length, "module")}
              </Badge>
            )}
            {(p.plos ?? []).length > 0 && (
              <Badge tone="ink" variant="outline">
                {countLabel((p.plos ?? []).length, "PLO")}
              </Badge>
            )}
          </>
        }
        aside={<HealthBadges stored={stored} />}
      />

      <ProgrammeVersions programme={p} />
      <ProgrammeOutcomes programme={p} />
      <ProgrammeModules programme={p} />
    </div>
  );
}

function renderViewerError(): React.ReactElement {
  return <ReportLoadError data-testid="viewer-error" />;
}

function NotInLibrary(): React.ReactElement {
  return (
    <EmptyState
      icon="folder-open"
      title="This programme is not in the library"
      description="It may have been removed since the library loaded."
      action={
        <LinkButton to="/reports/library" variant="outline" size="sm" icon="arrow-left">
          Back to the library
        </LinkButton>
      }
      data-testid="viewer-not-found"
    />
  );
}

export function ProgrammeViewer({ libraryId }: ProgrammeViewerProps): React.ReactElement {
  const { programmes, status, error: scopeError } = useReportScope();

  const stored = useMemo(
    () => programmes.find((programme) => programme.libraryId === libraryId) ?? null,
    [programmes, libraryId],
  );

  // A programme the caller cannot see reads as absent here, exactly as it does
  // from the API — the reports never disclose that it exists in another tenant.
  let state: AsyncState<StoredProgramme | null> = { status: "ready", data: stored };
  if (status === "loading") {
    state = { status: "loading" };
  } else if (scopeError) {
    state = { status: "error", error: scopeError };
  }

  return (
    <AsyncBoundary
      state={state}
      loadingLabel="Loading the programme…"
      loading={<FormPanelBones />}
      error={renderViewerError}
      empty={null}
    >
      {(found) => (found ? <ViewerBody stored={found} /> : <NotInLibrary />)}
    </AsyncBoundary>
  );
}

export default ProgrammeViewer;
